import { get, getBlob, post, patch, remove } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";

export type CalendarProvider = 'google_calendar' | 'microsoft_outlook';

export interface CalendarConnection {
  id: string;
  provider: CalendarProvider;
  email: string;
  connected_at: string;
}

export interface OAuthResponse {
  oauth_url: string;
  state: string;
}

export interface CalendarStatusResponse {
  calendars: CalendarConnection[];
  meetingAssistantEnabled: boolean;
}

export interface MeetingAssistantResponse {
  success: boolean;
  meetingAssistantEnabled: boolean;
}

export interface Meeting {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  platform: string | null;
  meetingUrl: string | null;
  botScheduled: boolean;
  botStatus: string | null;
  isCancelled: boolean;
  calendarId: string;
  summaryStatus: string | null; // 'processing' | 'completed' | 'failed' | null
}

// Meeting Summary Types
export type SummaryStatus = 'processing' | 'completed' | 'failed';

export interface KeyTopic {
  title: string;
  description: string;
}

export interface AssignedActions {
  assignee: string;
  actions: string[];
}

export interface ContractParty {
  role: string;
  name?: string;
  email?: string;
}

export interface ContractKeyTerm {
  term: string;
  value?: string;
}

export interface ContractMetadata {
  documentType: string;
  documentDescription: string;
  confidence: number;
  parties: ContractParty[];
  keyTerms: ContractKeyTerm[];
  relevantExcerpts: string[];
}

export interface MeetingAnalysis {
  summary: string;
  keyTopics: KeyTopic[];
  generalActions: string[];
  assignedActions: AssignedActions[];
}

export interface MeetingSummary {
  id: string;
  meetingId: string;
  transcriptId: string;
  status: SummaryStatus;
  analysis: MeetingAnalysis | null;
  contractDetected: boolean;
  contractMetadata: ContractMetadata | null;
  errorMessage: string | null;
  recipientEmail: string;
  hasContractDocx: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RecordingUrlResponse {
  videoUrl: string | null;
  available: boolean;
}

export const getCalendarStatus = (): Promise<ApiResponse<CalendarStatusResponse>> => {
  return get("/calendar/status", null);
};

export const initiateCalendarOAuth = (provider: CalendarProvider): Promise<ApiResponse<OAuthResponse>> => {
  return post("/calendar/oauth/initiate", { provider });
};

export const disconnectCalendar = (calendarId: string): Promise<ApiResponse<void>> => {
  return remove(`/calendar/${calendarId}`, null);
};

export const updateMeetingAssistant = (enabled: boolean): Promise<ApiResponse<MeetingAssistantResponse>> => {
  return patch("/user/self/meeting-assistant", { meetingAssistantEnabled: enabled }, null);
};

export const getMeetings = (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<ApiResponse<Meeting[]>> => {
  const queryParams = new URLSearchParams();
  if (params?.startDate) queryParams.append('startDate', params.startDate);
  if (params?.endDate) queryParams.append('endDate', params.endDate);
  
  const query = queryParams.toString();
  return get(`/calendar/meetings${query ? '?' + query : ''}`, null);
};

export const getMeetingSummary = (meetingId: string): Promise<ApiResponse<MeetingSummary>> => {
  return get(`/calendar/meetings/${meetingId}/summary`, null);
};

export const getMeetingRecordingUrl = (meetingId: string): Promise<ApiResponse<RecordingUrlResponse>> => {
  return get(`/calendar/meetings/${meetingId}/recording`, null);
};

export interface GenerateContractResponse {
  contractId: string;
  success: boolean;
}

export const generateContractFromMeeting = (meetingId: string): Promise<ApiResponse<GenerateContractResponse>> => {
  return post(`/calendar/meetings/${meetingId}/generate-contract`, {});
};

export interface SendSummaryEmailResponse {
  success: boolean;
}

export const sendMeetingSummaryEmail = (
  meetingId: string,
  includeContract?: boolean
): Promise<ApiResponse<SendSummaryEmailResponse>> => {
  return post(`/calendar/meetings/${meetingId}/send-summary-email`, {
    includeContract: includeContract ?? false,
  });
};

/**
 * Get the contract DOCX file as a blob for preview/download
 */
export const getMeetingContractDocx = (meetingId: string): Promise<ApiResponse<Blob>> => {
  return getBlob(`/calendar/meetings/${meetingId}/contract-docx`);
};

